import React from "react";
import CsI18n from "../../shared/csI18n";
import {
  Page,
  Layout,
  Card,
  Tabs,
  PageActions,
} from '@shopify/polaris';
import Env from "../../Env";
import GenerationRequest from "../../requests/GenerationRequest";
import "../../styles/generate.scss";
import LegalAppBridge from "../../LegalAppBridge";
import GenerationTabContent from "./GenerationTabContent";
import GenerationSkeleton from "./GenerationSkeleton";
import Replacement from "../../Replacement";
import Request from "../../requests/Request";

const
  preIsProd = 'is_prod_',
  preProdContent = 'prod_content_',
  preProdStyle = 'prod_style_',
  preDraftContent = 'draft_content_',
  preDraftStyle = 'draft_style_',
  preDraftOrgContent = 'draft_origin_content_',
  preDraftOrgStyle = 'draft_origin_style_',
  preManualEditing = 'manual_editing_',
  preDraftUnsaved = 'draft_unsaved_';

let editors = {};

export default class Generation extends React.Component {
  constructor(props) {
    super(props);
    this.allPages = [];
    this.tabs = [];
    this.state = {
      initPages: true,
      initConditions: true,
      initMentions: true,
      saving: false,
      publishing: false,
      tabSelected: 0,
      uploadToken: '',
      templates: {},
      validConditions: {},
      validReplacements: {},
      mentionPool: Replacement.getRuntimeMentions(),
    };
  }

  componentDidMount() {
    this.loadPageData();
    this.loadTemplates();
    this.loadUploadToken();
    this.loadConditions();
    this.loadMentionData();
  }

  loadPageData = () => {
    this.allPages = [];
    this.tabs = [];

    GenerationRequest.loadAllPages(
      (response) => {
        if (response.success) {
          let allPages = {},
            buildState = {};

          // Build all pages
          response.data.forEach(item => {
            allPages[item['shop_policy_type']] = { id: item['id'], name: item['name'] };
          });

          // Build all dynamic states
          for (let pageType in allPages) {
            Object.assign(buildState, {
              [preIsProd + pageType]: false,
              [preProdContent + pageType]: '',
              [preProdStyle + pageType]: '',
              [preDraftContent + pageType]: '',
              [preDraftStyle + pageType]: '',
              [preDraftOrgContent + pageType]: '',
              [preDraftOrgStyle + pageType]: '',
              [preManualEditing + pageType]: false,
              [preDraftUnsaved + pageType]: false,
            })
            // Build tabs, each tab is a page
            this.tabs.push({
              id: pageType.toLowerCase(),
              content: CsI18n.t(allPages[pageType]['name']),  // Translate page name
              panelID: pageType.split('_').join('-').toLowerCase(),
              pageType: pageType, // internal use
            });
          }

          // Fulfill dynamic states by response data
          response.data.forEach(item => {
            const
              pageType = item['shop_policy_type'],
              pageContent = item['content'],
              pageStyle = item['style'];
            if (item['is_production']) {
              buildState[preProdContent + pageType] = pageContent || '';  // Keep empty string instead of null
              buildState[preProdStyle + pageType] = pageStyle || '';      // CKEditor has problem with null content
            } else {
              buildState[preDraftContent + pageType] = pageContent || '';
              buildState[preDraftStyle + pageType] = pageStyle || '';
              buildState[preDraftOrgContent + pageType] = pageContent || '';
              buildState[preDraftOrgStyle + pageType] = pageStyle || '';
            }
          });

          // Save all pages
          this.allPages = allPages;
          // Save dynamic state
          this.setState(buildState);
        } else {
          LegalAppBridge.toastError(response.reason);
        }
        Env.log('Tabs', this.tabs);
      },
      () => {
        LegalAppBridge.toastError('Unknown error');
      },
      () => {
        this.setState({ initPages: false });
      }
    );
  };

  loadConditions = () => {
    GenerationRequest.loadConditions()
      .then(response => {
        if (response.success) {
          if (response.data) {
            this._mergeMentionPool(response.data.pool);
            this.setState({validConditions: response.data.condition});
          }
        } else {
          LegalAppBridge.toastError(response.reason);
        }
      })
      .catch(Request.catchError)
      .finally(() => this.setState({ initConditions: false }));
  }

  loadMentionData = () => {
    GenerationRequest.loadMentions()
      .then(response => {
        if (response.success) {
          if (response.data) {
            this._mergeMentionPool(response.data.pool);
            this.setState({validReplacements: response.data.valid})
          }
        } else {
          LegalAppBridge.toastError(response.reason);
        }
      })
      .catch(() => LegalAppBridge.toastError('Unknown error'))
      .finally(() => this.setState({ initMentions: false }));
  }

  _mergeMentionPool = (data) => {
    if (Array.isArray(data)) {
      const concatMentionPool = this.state.mentionPool.concat(
        data.filter(item => item.id && item.desc).map(item => ({
          id: item.id,
          desc: CsI18n.t(item.desc),
          type: item.type,
        }))
      );
      this.setState({
        mentionPool: concatMentionPool,
      });
    }
  }

  loadTemplates = () => {
    GenerationRequest.loadAllTemplates(
      (response) => {
        if (response.success && response.data) {
          this.setState({ templates: response.data });
        }
      }
    );
  }

  loadUploadToken = () => {
    GenerationRequest.fetchUploadToken().then(response => {
      if (response.token) {
        this.setState({ uploadToken: response.token });
      }
    });
  }

  handleEditorInitialized = (ckEditor, pageType) => {
    editors[pageType] = ckEditor;
    Env.log('Editors', editors);
  }

  handleEditorChange = (name, editor) => {
    this.setState({ [name]: editor.getData() });
  };

  handleChange = name => event => {
    if (event.type === 'click') {
      this.setState({ [name]: event.currentTarget ? event.currentTarget.id : event });
    } else {
      this.setState({ [name]: event.target ? event.target.value : event });
    }
  };

  handleChangeState = (name, value) => {
    this.setState({ [name]: value });
  }

  handleChangeEnv = pageType => event => {
    if ('click' === event.type) {
      const key = preIsProd + pageType,
        value = this.state[key];
      this.setState({ [key]: !value });
    }
  };

  handleDiscardChange = pageType => event => {
    if ('click' === event.type) {
      this.setState({
        [preDraftContent + pageType]: this.state[preDraftOrgContent + pageType],
        [preDraftStyle + pageType]: this.state[preDraftOrgStyle + pageType]
      });
    }
  };

  handleSavePage = pageType => event => {
    if ('click' !== event.type) {
      return;
    }

    const
      pageId = this.allPages[pageType]['id'],
      style = this.state[preDraftStyle + pageType],
      draftContent = editors[pageType].getData(),
      finalContent = this.replaceContentVariables(draftContent);

    this.setState({ saving: true });
    Promise.all([
      GenerationRequest.savePage({ content: draftContent, style, page_id: pageId, is_production: false }),
      GenerationRequest.savePage({ content: finalContent, style, page_id: pageId, is_production: true }),
    ]).then(response => {
      for (const res of response) {
        if (!res['success']) {
          LegalAppBridge.toastError(res['reason']);
          return;
        }
      }

      // Successful, back merge to state
      this.setState({
        [preDraftContent + pageType]: draftContent, [preDraftStyle + pageType]: style,
        [preProdContent + pageType]: finalContent, [preProdStyle + pageType]: style,
      });
      LegalAppBridge.toast('Save page successfully');
    }).catch(error => {
      Env.log('Save page error', error);
      LegalAppBridge.toastError('Save page error');
    }).finally(() => this.setState({ saving: false, [preDraftUnsaved + pageType]: false }));
  }

  replaceContentVariables = content => {
    const replacementEngine = new Replacement(content, this.state.validConditions, this.state.validReplacements, this.props.lang, Env.log);
    replacementEngine.replace();

    return replacementEngine.getContent();
  }

  handlePublishPage = pageType => {
    this.setState({ publishing: true });
    return GenerationRequest.publish({ page_id: this.allPages[pageType]['id'] })
      .finally(() => this.setState({ publishing: false }));
  }

  render() {
    const
      state = this.state,
      tabs = this.tabs,
      tabSelected = state.tabSelected;
    const debugActions = !Env.isDevMode() ? '' :
      <Layout>
        <Layout.Section>
          <PageActions
            secondaryActions={[
              { content: 'Reload templates', onAction: this.loadTemplates },
              { content: 'Reload conditions', onAction: this.loadConditions },
              { content: 'Reload mentions', onAction: this.loadMentionData },
            ]} />
        </Layout.Section>
      </Layout>;
    let tabContent = [];

    tabs.map(tab => {
      const pageType = tab.pageType;
      return tabContent.push(
        <GenerationTabContent
          preDraftContent={preDraftContent} editors={editors}
          pageId={this.allPages[pageType]['id']} pageType={pageType}
          isProduction={state[preIsProd + pageType]}
          prodContent={state[preProdContent + pageType]}
          prodStyle={state[preProdStyle + pageType]}
          draftContent={state[preDraftContent + pageType]}
          draftStyle={state[preDraftStyle + pageType]}
          manualEditing={state[preManualEditing + pageType]} manualEditingKey={preManualEditing + pageType}
          draftUnSaved={state[preDraftUnsaved + pageType]} draftUnSavedKey={preDraftUnsaved + pageType}
          templates={state.templates}
          saving={state.saving} publishing={state.publishing}
          uploadToken={state.uploadToken} mentionPool={state.mentionPool}
          handleChangeState={this.handleChangeState}
          handleChangeEnv={this.handleChangeEnv}
          handleDiscardChange={this.handleDiscardChange}
          handleSavePage={this.handleSavePage}
          handlePublishPage={this.handlePublishPage}
          handleEditorChange={this.handleEditorChange}
          handleEditorInitialized={this.handleEditorInitialized}
        />
      );
    });

    if (state.initPages || state.initConditions || state.initMentions || !tabs.length) {
      return <GenerationSkeleton />;
    }

    return (
      <Page fullWidth>
        <Layout>
          <Layout.AnnotatedSection
            title={CsI18n.t("generate.annotate.title.generate")}
            description={CsI18n.t('generate.annotate.desc.generate')}>
            <Layout.Section>
              <Card>
                <Tabs tabs={tabs} selected={tabSelected} onSelect={this.handleChange('tabSelected')}>
                  {tabContent[tabSelected]}
                </Tabs>
              </Card>
            </Layout.Section>
          </Layout.AnnotatedSection>
        </Layout>

        {debugActions}
      </Page>
    )
  }
}
